import { useState } from 'react';

import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Card from '@/components/Card';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import Form from '@/form';
import TextField from '@/form/TextField';
import OptionField from '@/form/OptionField';
import { Skill, SkillsAPI, SkillUpdateData } from '@/services/skillsAPI';

export interface Props {
  skill: Skill;
  hideModal: () => void;
  onSuccess: (updatedSkill: Skill) => void;
}

interface FormValues {
  display_name_en: string;
  display_name_es: string;
  ai_enabled: boolean;
  english_only: boolean;
  gravy_trained: boolean;
  requestable: boolean;
}

const SkillEditModal = ({ skill, hideModal, onSuccess }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initialValues: FormValues = {
    display_name_en: skill.display_name_en || '',
    display_name_es: skill.display_name_es || '',
    ai_enabled: skill.ai_enabled,
    english_only: skill.english_only,
    gravy_trained: skill.gravy_trained,
    requestable: skill.requestable,
  };

  const handleSubmit = async (values: FormValues) => {
    setIsLoading(true);
    setError(null);

    try {
      const updateData: SkillUpdateData = {
        display_name_en: values.display_name_en,
        display_name_es: values.display_name_es,
        ai_enabled: values.ai_enabled,
        english_only: values.english_only,
        gravy_trained: values.gravy_trained,
        requestable: values.requestable,
      };

      const response = await SkillsAPI.updateSkill(skill.id, updateData);

      if (response.success) {
        onSuccess(response.data);
        hideModal();
      } else {
        setError('Failed to update skill');
      }
    } catch (err: any) {
      console.error('Error updating skill:', err);
      setError(
        err.response?.data?.errors?.join(', ') ||
          err.response?.data?.error ||
          'Failed to update skill',
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      disableClickout
      size="sm"
      title={`Edit Skill: ${skill.name}`}
      onRequestClose={hideModal}
    >
      <Card.Section>
        {error && (
          <Alert
            description={error}
            status="danger"
            title="Error"
            onDismiss={() => setError(null)}
          />
        )}
        <Form initialValues={initialValues} onSubmit={handleSubmit}>
          <Stack gap={16} vertical>
            <TextField
              required
              fieldId="display_name_en"
              label="Display Name (English)"
              placeholder="Enter display name in English"
            />
            <TextField
              fieldId="display_name_es"
              label="Display Name (Spanish)"
              placeholder="Enter display name in Spanish"
            />
            <Stack gap={24}>
              <OptionField
                appearance="switch"
                fieldId="ai_enabled"
                label="AI Enabled"
              />
              <OptionField
                appearance="switch"
                fieldId="english_only"
                label="English Only"
              />
            </Stack>
            <Stack gap={24}>
              <OptionField
                appearance="switch"
                fieldId="gravy_trained"
                label="Gravy Trained"
              />
              <OptionField
                appearance="switch"
                fieldId="requestable"
                label="Requestable"
              />
            </Stack>
            <Stack justify="end">
              <Button
                a11yLabel="Cancel"
                appearance="outline"
                label="Cancel"
                onAction={hideModal}
              />
              <Button
                a11yLabel="Save skill"
                id="btn-submit"
                isLoading={isLoading}
                label="Save"
                type="submit"
              />
            </Stack>
          </Stack>
        </Form>
      </Card.Section>
    </Modal>
  );
};

export default SkillEditModal;
